import type {
  AbilityTuning,
  DifficultyDefinition,
  EnemyDefinition,
  ModuleDefinition,
  PlayerStats,
  StatBonuses,
  SynergyDefinition,
  SynergyRequirement,
  TeamTechDefinition,
} from "./phase-three-types";
import {
  describeExpandedModule,
  EXPANDED_MODULES,
  EXPANDED_SYNERGIES,
} from "./phase-three-expanded-content";
import skillOverrideDocument from "../content/skill-overrides.json";
import skillIdentityOverrideDocumentJson from "../content/skill-identity-overrides.json";
import skillPoolCurationDocumentJson from "../content/skill-pool-curation.json";
import skillRegradeDocumentJson from "../content/skill-regrades.json";
import {
  adaptSynergiesToModules,
  applySkillOverrides,
  applySynergyOverrides,
} from "./skill-overrides";
import {
  applySkillIdentityOverrides,
  applySkillIdentityOverridesToSynergies,
  getSkillIdentityOverrideId,
  type SkillIdentityOverrideDocument,
} from "./skill-identity-overrides";
import { isAllowedAttackIntervalMs } from "./attack-interval";
import {
  getModuleSkillRangeEntries,
  getSynergySkillRangeEntries,
  isAllowedSkillRange,
} from "./skill-range";
import {
  getModuleDpsErrors,
  normalizeModuleCatalogDps,
} from "./skill-dps";
import {
  createSkillPoolExpansion,
  expandSynergiesForSkillPool,
} from "./skill-pool-expansion";
import {
  applySkillPoolCuration,
  applySkillPoolCurationToSynergies,
  type SkillPoolCurationDocument,
} from "./skill-pool-curation";
import {
  applySkillRegrades,
  getSkillRegradeAdditions,
  type SkillRegradeDocument,
} from "./skill-regrades";
import {
  HIDDEN_EFFECTS,
  getHiddenEffect,
  validateHiddenEffectContent,
} from "./hidden-effects";
import {
  NEXT_SKILL_MODULES,
  validateNextSkillLibraryContent,
} from "./next-skill-library";
import { NEXT_SYNERGIES } from "./next-synergies";
import {
  UNDEAD_UNITS,
  validateUndeadUnitContent,
} from "./undead-units";

const skillIdentityOverrideDocument =
  skillIdentityOverrideDocumentJson as SkillIdentityOverrideDocument;
const skillPoolCurationDocument =
  skillPoolCurationDocumentJson as SkillPoolCurationDocument;
const skillRegradeDocument =
  skillRegradeDocumentJson as SkillRegradeDocument;

const BASE_MODULES: ModuleDefinition[] = [
  // 一阶：机制入口
  {
    id: "iron_sword",
    name: "铁剑",
    description: "朴素可靠的近战附魔，提高基础攻击。",
    tier: 1,
    category: "enhancement",
    tags: ["blade", "enhancement", "physical"],
    weight: 100,
    bonuses: { damageAdd: 8 },
  },
  {
    id: "ember_wand",
    name: "余烬短杖",
    description: "每0.8秒向射程内一个随机敌人发射余烬。",
    tier: 1,
    category: "attack",
    tags: ["fire", "projectile", "spell"],
    weight: 100,
    bonuses: {},
    combat: { damage: 16, intervalMs: 800, range: 430, target: "random" },
  },
  {
    id: "frost_orb",
    name: "霜纹法球",
    description: "周期性冻结一小片区域，并轻微减速敌人。",
    tier: 1,
    category: "area",
    tags: ["frost", "area", "spell"],
    weight: 100,
    bonuses: {},
    combat: {
      damage: 9,
      intervalMs: 900,
      range: 390,
      radius: 75,
      target: "random_area",
      slowRatio: 0.12,
      slowDurationMs: 1_200,
    },
  },
  {
    id: "hunter_bolt",
    name: "猎魔弩矢",
    description: "只寻找精英与首领，命中时造成高额伤害。",
    tier: 1,
    category: "elite_attack",
    tags: ["elite_hunter", "projectile", "physical"],
    weight: 88,
    bonuses: {},
    combat: { damage: 55, intervalMs: 1_600, range: 650, target: "elite_only" },
  },
  {
    id: "menders_needle",
    name: "缝魂针",
    description: "持续缝合自身的裂隙。",
    tier: 1,
    category: "healing",
    tags: ["healing", "life", "support"],
    weight: 92,
    bonuses: { maxHealthAdd: 25 },
    healing: { target: "self", amount: 4, intervalMs: 1_000 },
  },
  {
    id: "mooncalf",
    name: "月绒幼兽",
    description: "一只不会攻击的牧灵跟随左右，每10秒带回金币。",
    tier: 1,
    category: "pasture",
    tags: ["pasture", "beast", "economy"],
    weight: 82,
    bonuses: {},
    companion: {
      kind: "pasture",
      presentationId: "companion.mooncalf",
      count: 1,
      orbitRadius: 115,
      moveSpeed: 85,
      incomeIntervalMs: 10_000,
      incomeGold: 7,
    },
  },
  {
    id: "copper_familiar",
    name: "铜羽魔宠",
    description: "一只绕行的魔宠自行攻击随机敌人。",
    tier: 1,
    category: "follower",
    tags: ["follower", "construct", "projectile"],
    weight: 90,
    bonuses: {},
    companion: {
      kind: "follower",
      presentationId: "companion.copper_familiar",
      count: 1,
      orbitRadius: 145,
      moveSpeed: 120,
      damage: 6,
      intervalMs: 400,
      range: 430,
    },
  },
  {
    id: "spark_totem",
    name: "火花图腾",
    description: "魔力充足时周期召唤会死亡的火花精怪。",
    tier: 1,
    category: "summon",
    tags: ["summon", "fire", "mana"],
    weight: 86,
    bonuses: { manaMaxAdd: 15 },
    companion: {
      kind: "summon",
      presentationId: "summon.spark_imp",
      count: 1,
      orbitRadius: 190,
      moveSpeed: 145,
      damage: 9,
      intervalMs: 700,
      range: 260,
      health: 45,
      armor: 0,
      manaCost: 12,
      spawnIntervalMs: 8_000,
    },
  },
  {
    id: "oak_guard",
    name: "橡木护卫",
    description: "召来可死亡、会追随玩家且能够复活的木灵护卫。",
    tier: 1,
    category: "guard",
    tags: ["guard", "nature", "melee"],
    weight: 82,
    bonuses: {},
    companion: {
      kind: "guard",
      presentationId: "guard.oak",
      count: 1,
      orbitRadius: 105,
      moveSpeed: 110,
      damage: 13,
      intervalMs: 900,
      range: 105,
      health: 130,
      armor: 4,
      respawnMs: 12_000,
    },
  },
  {
    id: "miners_lens",
    name: "矿脉透镜",
    description: "扩大灵视范围，并提高遗迹勘探收益。",
    tier: 1,
    category: "archaeology",
    tags: ["archaeology", "sight", "economy"],
    weight: 80,
    bonuses: { revealRadius: 90, archaeologyMultiplier: 0.18 },
    archaeology: { revealRadius: 90, yieldMultiplier: 0.18, wageShare: 0.02 },
  },
  {
    id: "field_ledger",
    name: "行商账簿",
    description: "每12秒获得金币和少量灵息。",
    tier: 1,
    category: "finance",
    tags: ["finance", "economy", "guild"],
    weight: 78,
    bonuses: {},
    economy: { trigger: "periodic", intervalMs: 12_000, gold: 8, experience: 3 },
  },
  {
    id: "journeyman_gloves",
    name: "学徒手套",
    description: "每完成一次有效攻击节奏，获得少量金币和灵息。",
    tier: 1,
    category: "labor",
    tags: ["labor", "craft", "economy"],
    weight: 86,
    bonuses: {},
    economy: { trigger: "attack", intervalMs: 2_000, gold: 2, experience: 2 },
  },
  {
    id: "vitality_script",
    name: "活力卷页",
    description: "获得时立即提高本局生命上限；再次获得可重复触发。",
    tier: 1,
    category: "one_shot",
    tags: ["one_shot", "life", "scroll"],
    weight: 76,
    bonuses: {},
    onAcquire: { maxHealth: 70 },
  },
  {
    id: "patient_heart",
    name: "耐心之心",
    description: "每存活20秒，局内永久增加生命上限。",
    tier: 1,
    category: "growth",
    tags: ["growth", "life", "nature"],
    weight: 84,
    bonuses: {},
    growth: { event: "second", every: 20, stat: "maxHealth", amount: 5, cap: 150 },
  },
  {
    id: "lucky_coin",
    name: "命运反面",
    description: "极低概率出现；获得时立即得到大量金币与一枚结晶。",
    tier: 1,
    category: "one_shot",
    tags: ["one_shot", "hidden", "fortune"],
    weight: 5,
    hidden: true,
    bonuses: {},
    onAcquire: { gold: 140, crystals: 1 },
  },

  // 二阶：构筑方向成形
  {
    id: "steel_blade",
    name: "钢刃",
    description: "铁匠反复锻打的利刃，显著提高基础攻击。",
    tier: 2,
    category: "enhancement",
    tags: ["blade", "enhancement", "physical"],
    weight: 100,
    bonuses: { damageAdd: 18, fireIntervalMultiplier: 0.96 },
  },
  {
    id: "firebolt_codex",
    name: "火矢秘典",
    description: "快速发射能够穿透一个目标的火矢。",
    tier: 2,
    category: "attack",
    tags: ["fire", "projectile", "spell"],
    weight: 100,
    bonuses: {},
    combat: {
      damage: 24,
      intervalMs: 600,
      range: 520,
      target: "random",
      pierceTargets: 1,
    },
  },
  {
    id: "glacier_bell",
    name: "冰川鸣钟",
    description: "钟声在大范围内造成伤害并减速。",
    tier: 2,
    category: "area",
    tags: ["frost", "area", "relic"],
    weight: 98,
    bonuses: {},
    combat: {
      damage: 18,
      intervalMs: 1_000,
      range: 500,
      radius: 115,
      target: "random_area",
      slowRatio: 0.2,
      slowDurationMs: 1_600,
    },
  },
  {
    id: "giant_slayer",
    name: "巨兽穿心枪",
    description: "只攻击精英与首领，伤害随猎手专精提高。",
    tier: 2,
    category: "elite_attack",
    tags: ["elite_hunter", "pierce", "physical"],
    weight: 90,
    bonuses: { eliteDamageMultiplier: 0.15 },
    combat: { damage: 115, intervalMs: 1_700, range: 760, target: "elite_only" },
  },
  {
    id: "silver_thread",
    name: "银月丝线",
    description: "周期治疗附近生命比例最低的队友。",
    tier: 2,
    category: "healing",
    tags: ["healing", "moon", "support"],
    weight: 92,
    bonuses: { healingPerSecond: 2 },
    healing: { target: "team", amount: 16, intervalMs: 2_400, range: 420 },
  },
  {
    id: "shepherd_bell",
    name: "牧灵法铃",
    description: "增加一只牧灵，并加快所有牧灵带回资源的节奏。",
    tier: 2,
    category: "pasture",
    tags: ["pasture", "beast", "economy"],
    weight: 84,
    bonuses: { companionDamageMultiplier: 0.08 },
    companion: {
      kind: "pasture",
      presentationId: "companion.star_lamb",
      count: 2,
      orbitRadius: 135,
      moveSpeed: 95,
      incomeIntervalMs: 9_000,
      incomeGold: 8,
    },
  },
  {
    id: "owl_familiar",
    name: "星瞳枭使",
    description: "远程魔宠，能够同时寻找两个目标。",
    tier: 2,
    category: "follower",
    tags: ["follower", "arcane", "projectile"],
    weight: 92,
    bonuses: { rangeAdd: 20 },
    companion: {
      kind: "follower",
      presentationId: "companion.owl",
      count: 1,
      orbitRadius: 165,
      moveSpeed: 135,
      damage: 12,
      intervalMs: 350,
      range: 560,
    },
  },
  {
    id: "imp_contract",
    name: "小恶魔契约",
    description: "持续消耗魔力召唤两只会死亡的小恶魔。",
    tier: 2,
    category: "summon",
    tags: ["summon", "fire", "mana"],
    weight: 88,
    bonuses: { manaRegenAdd: 1 },
    companion: {
      kind: "summon",
      presentationId: "summon.imp",
      count: 2,
      orbitRadius: 220,
      moveSpeed: 165,
      damage: 16,
      intervalMs: 700,
      range: 300,
      health: 65,
      armor: 2,
      manaCost: 18,
      spawnIntervalMs: 9_000,
    },
  },
  {
    id: "rune_sentinel",
    name: "符文哨卫",
    description: "带护甲的护卫，死亡后会在玩家身边重组。",
    tier: 2,
    category: "guard",
    tags: ["guard", "construct", "rune"],
    weight: 86,
    bonuses: { armorAdd: 4 },
    companion: {
      kind: "guard",
      presentationId: "guard.rune",
      count: 1,
      orbitRadius: 120,
      moveSpeed: 125,
      damage: 23,
      intervalMs: 800,
      range: 125,
      health: 240,
      armor: 10,
      respawnMs: 10_000,
    },
  },
  {
    id: "appraisers_monocle",
    name: "鉴宝师单镜",
    description: "提高矿脉收益，并将更多勘探收入计入每分钟分红。",
    tier: 2,
    category: "archaeology",
    tags: ["archaeology", "sight", "finance"],
    weight: 82,
    bonuses: { revealRadius: 110, archaeologyMultiplier: 0.3, wageShareAdd: 0.04 },
    archaeology: { revealRadius: 110, yieldMultiplier: 0.3, wageShare: 0.04 },
  },
  {
    id: "guild_bond",
    name: "行会债券",
    description: "每分钟派息；附近有队友时派息更高。",
    tier: 2,
    category: "finance",
    tags: ["finance", "guild", "team"],
    weight: 78,
    bonuses: { wageShareAdd: 0.02 },
    economy: {
      trigger: "periodic",
      intervalMs: 15_000,
      gold: 13,
      experience: 4,
      condition: "near_ally",
    },
  },
  {
    id: "tempered_core",
    name: "百炼本源",
    description: "每完成25次有效攻击，局内永久增加攻击力。",
    tier: 2,
    category: "growth",
    tags: ["growth", "attack", "craft"],
    weight: 86,
    bonuses: {},
    growth: { event: "attack", every: 25, stat: "damage", amount: 3, cap: 90 },
  },
  {
    id: "fey_bargain",
    name: "妖精的偏爱",
    description: "罕见秘印；大幅提高金币收入，但降低生命上限。",
    tier: 2,
    category: "special",
    tags: ["hidden", "finance", "fey"],
    weight: 5,
    hidden: true,
    bonuses: { goldMultiplier: 0.5, maxHealthAdd: -70, moveSpeedAdd: 18 },
  },

  // 三阶：跨系统联动
  {
    id: "dwarven_whetstone",
    name: "矮人磨刀石",
    description: "有效攻击会支付金币与灵息，且提高物理构筑效率。",
    tier: 3,
    category: "labor",
    tags: ["labor", "craft", "blade"],
    weight: 94,
    bonuses: { damageAdd: 12 },
    economy: { trigger: "attack", intervalMs: 1_500, gold: 4, experience: 4 },
  },
  {
    id: "storm_lantern",
    name: "雷暴提灯",
    description: "闪电会在多个随机目标之间跳跃。",
    tier: 3,
    category: "attack",
    tags: ["lightning", "chain", "spell"],
    weight: 100,
    bonuses: {},
    combat: {
      damage: 42,
      intervalMs: 700,
      range: 620,
      target: "random",
      chainTargets: 3,
    },
  },
  {
    id: "plague_censer",
    name: "疫雾香炉",
    description: "持续向随机区域投放覆盖广阔的腐化疫雾。",
    tier: 3,
    category: "area",
    tags: ["poison", "area", "curse"],
    weight: 96,
    bonuses: {},
    combat: {
      damage: 26,
      intervalMs: 700,
      range: 560,
      radius: 155,
      target: "random_area",
    },
  },
  {
    id: "basilisk_needle",
    name: "蛇王毒针",
    description: "对精英与首领发射三枚毒针。",
    tier: 3,
    category: "elite_attack",
    tags: ["elite_hunter", "poison", "projectile"],
    weight: 90,
    bonuses: { eliteDamageMultiplier: 0.25 },
    combat: {
      damage: 72,
      intervalMs: 1_400,
      range: 720,
      target: "elite_only",
      projectiles: 3,
    },
  },
  {
    id: "spring_chalice",
    name: "春泉圣杯",
    description: "持续治疗自身，并周期治疗全队。",
    tier: 3,
    category: "healing",
    tags: ["healing", "nature", "relic"],
    weight: 90,
    bonuses: { healingPerSecond: 5, maxHealthAdd: 70 },
    healing: { target: "team", amount: 24, intervalMs: 2_000, range: 650 },
  },
  {
    id: "wild_pact",
    name: "荒野契约",
    description: "牧灵可以继承玩家一个攻击秘印的部分伤害与频次。",
    tier: 3,
    category: "pasture",
    tags: ["pasture", "beast", "projectile"],
    weight: 80,
    bonuses: { companionDamageMultiplier: 0.25 },
    special: "pasture_inherits_attack",
  },
  {
    id: "raven_orbit",
    name: "群鸦星环",
    description: "三只群鸦魔宠环绕玩家并高速攻击。",
    tier: 3,
    category: "follower",
    tags: ["follower", "shadow", "rapid"],
    weight: 90,
    bonuses: {},
    companion: {
      kind: "follower",
      presentationId: "companion.raven",
      count: 3,
      orbitRadius: 180,
      moveSpeed: 165,
      damage: 13,
      intervalMs: 300,
      range: 540,
    },
  },
  {
    id: "bone_legion",
    name: "白骨军册",
    description: "持续召唤有护甲的骸骨士兵，最多形成一支小队。",
    tier: 3,
    category: "summon",
    tags: ["summon", "death", "mana"],
    weight: 86,
    bonuses: { summonCapAdd: 2, manaMaxAdd: 30 },
    companion: {
      kind: "summon",
      presentationId: "summon.skeleton",
      count: 3,
      orbitRadius: 260,
      moveSpeed: 150,
      damage: 24,
      intervalMs: 700,
      range: 120,
      health: 115,
      armor: 8,
      manaCost: 24,
      spawnIntervalMs: 10_000,
    },
  },
  {
    id: "ancestral_guard",
    name: "先祖战魂",
    description: "强大的近战护卫，复活时治疗主人。",
    tier: 3,
    category: "guard",
    tags: ["guard", "spirit", "healing"],
    weight: 84,
    bonuses: { healingPerSecond: 2 },
    companion: {
      kind: "guard",
      presentationId: "guard.ancestor",
      count: 1,
      orbitRadius: 130,
      moveSpeed: 145,
      damage: 48,
      intervalMs: 800,
      range: 130,
      health: 420,
      armor: 16,
      respawnMs: 9_000,
    },
  },
  {
    id: "echoing_map",
    name: "回声藏宝图",
    description: "显著扩大灵视；每次勘探都会增加后续工资。",
    tier: 3,
    category: "archaeology",
    tags: ["archaeology", "map", "finance"],
    weight: 78,
    bonuses: {
      revealRadius: 180,
      archaeologyMultiplier: 0.5,
      wageShareAdd: 0.07,
    },
    archaeology: { revealRadius: 180, yieldMultiplier: 0.5, wageShare: 0.07 },
  },
  {
    id: "mimic_contract",
    name: "宝箱怪契约",
    description: "罕见秘印；获得时复制一个随机已持有秘印的静态强化。",
    tier: 3,
    category: "special",
    tags: ["hidden", "special", "chest"],
    weight: 4,
    hidden: true,
    bonuses: { damageAdd: 25, maxHealthAdd: 120, goldMultiplier: 0.15 },
    special: "copy_random_ability",
  },

  // 四阶：钥匙、规则改变与终局准备
  {
    id: "dragonsteel_edge",
    name: "龙钢锋刃",
    description: "龙火锻造的重刃，同时强化攻击与暴击节奏。",
    tier: 4,
    category: "enhancement",
    tags: ["blade", "enhancement", "fire"],
    weight: 100,
    bonuses: { damageAdd: 55, damageMultiplier: 0.12, fireIntervalMultiplier: 0.9 },
  },
  {
    id: "meteor_seal",
    name: "陨星秘印",
    description: "周期召来大范围陨星，对随机区域造成毁灭打击。",
    tier: 4,
    category: "area",
    tags: ["fire", "area", "celestial"],
    weight: 96,
    bonuses: {},
    combat: {
      damage: 155,
      intervalMs: 2_000,
      range: 820,
      radius: 210,
      target: "random_area",
    },
  },
  {
    id: "winter_court",
    name: "凛冬王庭",
    description: "高频冻结随机区域，大幅减缓敌群。",
    tier: 4,
    category: "area",
    tags: ["frost", "area", "royal"],
    weight: 94,
    bonuses: {},
    combat: {
      damage: 56,
      intervalMs: 700,
      range: 680,
      radius: 175,
      target: "random_area",
      slowRatio: 0.32,
      slowDurationMs: 2_000,
    },
  },
  {
    id: "royal_hunt",
    name: "王猎宣告",
    description: "罕见秘印；周期处决普通敌人，并重创精英。",
    tier: 4,
    category: "elite_attack",
    tags: ["hidden", "elite_hunter", "royal"],
    weight: 5,
    hidden: true,
    bonuses: { eliteDamageMultiplier: 0.45 },
    combat: {
      damage: 283,
      intervalMs: 2_500,
      range: 900,
      target: "elite_only",
      executeNormalEveryMs: 18_000,
    },
  },
  {
    id: "worldroot_growth",
    name: "世界根须",
    description: "每次勘探或牧灵产出都会使本体永久成长。",
    tier: 4,
    category: "growth",
    tags: ["growth", "nature", "archaeology", "pasture"],
    weight: 84,
    bonuses: { maxHealthAdd: 180 },
    growth: { event: "excavate", every: 1, stat: "maxHealth", amount: 12, cap: 420 },
  },
  {
    id: "crimson_key",
    name: "赤印秘钥",
    description: "可以开启地图上的赤红秘藏。",
    tier: 4,
    category: "archaeology",
    tags: ["archaeology", "key", "fire"],
    weight: 72,
    bonuses: { archaeologyMultiplier: 0.25 },
    archaeology: { chestKey: "crimson", yieldMultiplier: 0.25 },
  },
  {
    id: "azure_key",
    name: "苍印秘钥",
    description: "可以开启地图上的苍蓝秘藏。",
    tier: 4,
    category: "archaeology",
    tags: ["archaeology", "key", "frost"],
    weight: 72,
    bonuses: { manaMaxAdd: 60 },
    archaeology: { chestKey: "azure", yieldMultiplier: 0.2 },
  },
  {
    id: "verdant_key",
    name: "翠印秘钥",
    description: "可以开启地图上的翠绿秘藏。",
    tier: 4,
    category: "archaeology",
    tags: ["archaeology", "key", "nature"],
    weight: 72,
    bonuses: { maxHealthAdd: 160 },
    archaeology: { chestKey: "verdant", yieldMultiplier: 0.2 },
  },
  {
    id: "golden_key",
    name: "鎏金秘钥",
    description: "可以开启地图上的鎏金秘藏。",
    tier: 4,
    category: "archaeology",
    tags: ["archaeology", "key", "fortune"],
    weight: 66,
    bonuses: { goldMultiplier: 0.2, wageShareAdd: 0.03 },
    archaeology: { chestKey: "golden", yieldMultiplier: 0.35, wageShare: 0.03 },
  },

  // 五阶：终局构筑核心
  {
    id: "phoenix_horn",
    name: "凤凰号角",
    description: "召唤浴火护卫；死亡后复活并灼烧周围敌人。",
    tier: 5,
    category: "guard",
    tags: ["guard", "fire", "rebirth", "healing"],
    weight: 100,
    bonuses: { healingPerSecond: 5, maxHealthAdd: 240 },
    companion: {
      kind: "guard",
      presentationId: "guard.phoenix",
      count: 1,
      orbitRadius: 165,
      moveSpeed: 190,
      damage: 130,
      intervalMs: 500,
      range: 520,
      health: 900,
      armor: 22,
      respawnMs: 7_000,
    },
    special: "companion_rebirth_burst",
    evolutionTo: "ashen_crown",
  },
  {
    id: "worldtree_compass",
    name: "世界树罗盘",
    description: "揭示广阔区域内的矿脉，并让遗迹收益持续分红。",
    tier: 5,
    category: "archaeology",
    tags: ["archaeology", "nature", "finance", "map"],
    weight: 96,
    bonuses: {
      revealRadius: 420,
      archaeologyMultiplier: 1,
      wageShareAdd: 0.12,
    },
    archaeology: { revealRadius: 420, yieldMultiplier: 1, wageShare: 0.12 },
    evolutionTo: "atlas_of_secrets",
  },
  {
    id: "storm_crown",
    name: "风暴王冠",
    description: "高频雷击在敌群中连续跳跃。",
    tier: 5,
    category: "attack",
    tags: ["lightning", "chain", "royal", "spell"],
    weight: 100,
    bonuses: { fireIntervalMultiplier: 0.9 },
    combat: {
      damage: 90,
      intervalMs: 400,
      range: 820,
      target: "random",
      chainTargets: 6,
    },
    evolutionTo: "eye_of_tempest",
  },
  {
    id: "demon_forge",
    name: "魔神熔炉",
    description: "将金币、战斗与成长熔为一体，极大强化本体。",
    tier: 5,
    category: "enhancement",
    tags: ["enhancement", "growth", "fire", "craft"],
    weight: 96,
    bonuses: {
      damageAdd: 120,
      damageMultiplier: 0.22,
      maxHealthAdd: 420,
      armorAdd: 28,
    },
    growth: { event: "kill", every: 40, stat: "damage", amount: 12, cap: 180 },
    evolutionTo: "worldbreaker_relic",
  },
  {
    id: "celestial_menagerie",
    name: "星界牧园",
    description: "召来三只星兽牧灵；它们带回资源并继承攻击秘印。",
    tier: 5,
    category: "pasture",
    tags: ["pasture", "beast", "celestial", "projectile"],
    weight: 94,
    bonuses: { companionDamageMultiplier: 0.55, goldMultiplier: 0.18 },
    companion: {
      kind: "pasture",
      presentationId: "companion.star_beast",
      count: 3,
      orbitRadius: 210,
      moveSpeed: 145,
      incomeIntervalMs: 7_000,
      incomeGold: 18,
    },
    special: "pasture_inherits_attack",
    evolutionTo: "wild_hunt",
  },
  {
    id: "archmage_gate",
    name: "大法师之门",
    description: "魔力充足时持续召来元素军团。",
    tier: 5,
    category: "summon",
    tags: ["summon", "arcane", "mana", "elemental"],
    weight: 94,
    bonuses: { manaMaxAdd: 140, manaRegenAdd: 4, summonCapAdd: 5 },
    companion: {
      kind: "summon",
      presentationId: "summon.elemental",
      count: 4,
      orbitRadius: 320,
      moveSpeed: 185,
      damage: 88,
      intervalMs: 600,
      range: 430,
      health: 360,
      armor: 18,
      manaCost: 45,
      spawnIntervalMs: 12_000,
    },
    evolutionTo: "throne_of_legions",
  },

  // 六阶：固定升格，不能重铸
  {
    id: "ashen_crown",
    name: "不灭灰烬王冠",
    description: "凤凰化为不灭双生护卫；每次重生都会爆发治疗与火焰。",
    tier: 6,
    category: "guard",
    tags: ["guard", "fire", "rebirth", "healing", "mythic"],
    weight: 0,
    bonuses: { healingPerSecond: 12, maxHealthAdd: 520, damageMultiplier: 0.12 },
    companion: {
      kind: "guard",
      presentationId: "guard.ashen_phoenix",
      count: 2,
      orbitRadius: 185,
      moveSpeed: 220,
      damage: 230,
      intervalMs: 400,
      range: 680,
      health: 1_500,
      armor: 35,
      respawnMs: 4_500,
    },
    special: "companion_rebirth_burst",
    evolutionFrom: "phoenix_horn",
  },
  {
    id: "atlas_of_secrets",
    name: "万秘图鉴",
    description: "揭示全部遗迹，视作持有四把秘钥，并将勘探转化为巨额分红。",
    tier: 6,
    category: "archaeology",
    tags: ["archaeology", "nature", "finance", "map", "mythic"],
    weight: 0,
    bonuses: {
      revealRadius: 1_500,
      archaeologyMultiplier: 2,
      wageShareAdd: 0.24,
      goldMultiplier: 0.25,
    },
    archaeology: { revealRadius: 1_500, yieldMultiplier: 2, wageShare: 0.24 },
    special: "all_chest_keys",
    evolutionFrom: "worldtree_compass",
  },
  {
    id: "eye_of_tempest",
    name: "风暴之眼",
    description: "闪电覆盖战场，并周期打开持续轰击的雷霆裂隙。",
    tier: 6,
    category: "attack",
    tags: ["lightning", "chain", "royal", "spell", "mythic"],
    weight: 0,
    bonuses: { fireIntervalMultiplier: 0.78, damageMultiplier: 0.18 },
    combat: {
      damage: 170,
      intervalMs: 300,
      range: 1_050,
      target: "random",
      chainTargets: 9,
      radius: 70,
    },
    special: "portal_on_hit",
    evolutionFrom: "storm_crown",
  },
  {
    id: "worldbreaker_relic",
    name: "碎界圣遗物",
    description: "巨幅强化本体，并在生命危急时发动无消耗过载。",
    tier: 6,
    category: "enhancement",
    tags: ["enhancement", "growth", "fire", "craft", "mythic"],
    weight: 0,
    bonuses: {
      damageAdd: 280,
      damageMultiplier: 0.42,
      maxHealthAdd: 900,
      armorAdd: 55,
      manaRegenAdd: 4,
    },
    growth: { event: "kill", every: 25, stat: "damage", amount: 18, cap: 360 },
    special: "emergency_overdrive",
    evolutionFrom: "demon_forge",
  },
  {
    id: "wild_hunt",
    name: "永夜荒猎",
    description: "五只神话星兽驰骋战场，完整继承一个攻击秘印。",
    tier: 6,
    category: "pasture",
    tags: ["pasture", "beast", "celestial", "projectile", "mythic"],
    weight: 0,
    bonuses: { companionDamageMultiplier: 1, goldMultiplier: 0.32 },
    companion: {
      kind: "pasture",
      presentationId: "companion.wild_hunt",
      count: 5,
      orbitRadius: 270,
      moveSpeed: 205,
      incomeIntervalMs: 6_000,
      incomeGold: 25,
    },
    special: "pasture_inherits_attack",
    evolutionFrom: "celestial_menagerie",
  },
  {
    id: "throne_of_legions",
    name: "万军王座",
    description: "召唤物成群出现并留下短暂战斗回响。",
    tier: 6,
    category: "summon",
    tags: ["summon", "arcane", "mana", "elemental", "mythic"],
    weight: 0,
    bonuses: {
      manaMaxAdd: 260,
      manaRegenAdd: 8,
      summonCapAdd: 10,
      companionDamageMultiplier: 0.5,
    },
    companion: {
      kind: "summon",
      presentationId: "summon.legion",
      count: 6,
      orbitRadius: 380,
      moveSpeed: 220,
      damage: 140,
      intervalMs: 400,
      range: 520,
      health: 620,
      armor: 28,
      manaCost: 65,
      spawnIntervalMs: 10_000,
    },
    special: "summon_echoes",
    evolutionFrom: "archmage_gate",
  },
];

const PRE_EXPANSION_MODULES: ModuleDefinition[] = [
  ...BASE_MODULES,
  ...EXPANDED_MODULES,
  ...getSkillRegradeAdditions(skillRegradeDocument),
];

const SKILL_POOL_EXPANSION = createSkillPoolExpansion(
  PRE_EXPANSION_MODULES,
);

const REGRADED_PRE_IDENTITY_MODULES = applySkillRegrades(
  [...PRE_EXPANSION_MODULES, ...SKILL_POOL_EXPANSION],
  skillRegradeDocument,
);
const NORMALIZED_PRE_IDENTITY_MODULES = normalizeModuleCatalogDps(
  REGRADED_PRE_IDENTITY_MODULES,
);
const CURATED_PRE_IDENTITY_MODULES = applySkillPoolCuration(
  NORMALIZED_PRE_IDENTITY_MODULES,
  skillPoolCurationDocument,
);
const NORMALIZED_DEFAULT_MODULES = applySkillIdentityOverrides(
  CURATED_PRE_IDENTITY_MODULES,
  skillIdentityOverrideDocument,
);
const generatedModuleIds = new Set(
  [...EXPANDED_MODULES, ...SKILL_POOL_EXPANSION].flatMap((module) => {
    const moduleId = getSkillIdentityOverrideId(
      module.id,
      skillIdentityOverrideDocument,
    );
    return moduleId ? [moduleId] : [];
  }),
);
const normalizedDefaultById = new Map(
  NORMALIZED_DEFAULT_MODULES.map((module) => [module.id, module]),
);

export const RETIRED_DEFAULT_MODULES: ModuleDefinition[] =
  NORMALIZED_DEFAULT_MODULES.map((module) => {
    let description = generatedModuleIds.has(module.id)
      ? describeExpandedModule(module)
      : module.description;
    if (module.evolutionTo) {
      const target = normalizedDefaultById.get(module.evolutionTo);
      description += target ? ` 可固定升格为「${target.name}」。` : "";
    }
    if (module.evolutionFrom) {
      const source = normalizedDefaultById.get(module.evolutionFrom);
      description = source
        ? `由「${source.name}」唯一升格。${description}`
        : description;
    }
    return { ...module, description };
  });

export const RETIRED_MODULES: ModuleDefinition[] = applySkillOverrides(
  RETIRED_DEFAULT_MODULES,
  skillOverrideDocument,
);

// P3.9 and earlier definitions remain resolvable for archived reports and
// migration tests, but they are no longer part of offers, the codex, or builds.
export const DEFAULT_MODULES: ModuleDefinition[] = NEXT_SKILL_MODULES;
export const MODULES: ModuleDefinition[] = applySkillOverrides(
  DEFAULT_MODULES,
  skillOverrideDocument,
);
export const ABILITIES = MODULES;

const BASE_SYNERGIES: SynergyDefinition[] = [
  {
    id: "smiths_oath",
    name: "铸剑师的誓约",
    description: "锻造链条完整后，物理攻击加速并获得额外攻击力。",
    requirements: [
      { kind: "module", moduleId: "iron_sword" },
      { kind: "module", moduleId: "steel_blade" },
      { kind: "module", moduleId: "dwarven_whetstone" },
    ],
    bonuses: { damageAdd: 35, fireIntervalMultiplier: 0.86 },
  },
  {
    id: "ember_covenant",
    name: "余烬盟约",
    description: "三种火焰秘印使所有伤害附带更强的爆裂。",
    requirements: [{ kind: "tag_count", tag: "fire", count: 3 }],
    bonuses: { damageMultiplier: 0.18, splashRadius: 65 },
  },
  {
    id: "winter_chorus",
    name: "凛冬合唱",
    description: "寒霜秘印共同降低攻击间隔并扩大范围。",
    requirements: [{ kind: "tag_count", tag: "frost", count: 2 }],
    bonuses: { fireIntervalMultiplier: 0.88, rangeAdd: 90, armorAdd: 12 },
  },
  {
    id: "royal_monster_hunt",
    name: "王家猎魔令",
    description: "猎魔装备集中火力，显著提高对精英和首领的伤害。",
    requirements: [{ kind: "tag_count", tag: "elite_hunter", count: 2 }],
    bonuses: { eliteDamageMultiplier: 0.6, rangeAdd: 100 },
  },
  {
    id: "mercy_circle",
    name: "慈悲守环",
    description: "治疗与护卫互相呼应，提供持续恢复与护甲。",
    requirements: [
      { kind: "tag_count", tag: "healing", count: 2 },
      { kind: "tag_count", tag: "guard", count: 1 },
    ],
    bonuses: { healingPerSecond: 8, armorAdd: 18 },
  },
  {
    id: "wild_roaming",
    name: "荒野巡游",
    description: "牧灵获得装备中攻击秘印的一部分火力。",
    requirements: [
      { kind: "tag_count", tag: "pasture", count: 2 },
      { kind: "tag_count", tag: "projectile", count: 1 },
    ],
    bonuses: { companionDamageMultiplier: 0.5, goldMultiplier: 0.12 },
    special: "pasture_inherits_attack",
  },
  {
    id: "familiar_constellation",
    name: "使魔星座",
    description: "多个随从组成环形阵列，提高随从伤害与主人射程。",
    requirements: [{ kind: "tag_count", tag: "follower", count: 2 }],
    bonuses: { companionDamageMultiplier: 0.45, rangeAdd: 75 },
  },
  {
    id: "pact_army",
    name: "契约军势",
    description: "召唤契约共用魔力，提高上限、恢复与军团规模。",
    requirements: [
      { kind: "tag_count", tag: "summon", count: 2 },
      { kind: "tag_count", tag: "mana", count: 2 },
    ],
    bonuses: { manaMaxAdd: 80, manaRegenAdd: 3, summonCapAdd: 4 },
  },
  {
    id: "immortal_watch",
    name: "不朽守望",
    description: "两名以上护卫在场时，主人获得护甲与生命。",
    requirements: [{ kind: "tag_count", tag: "guard", count: 2 }],
    bonuses: { armorAdd: 28, maxHealthAdd: 260, companionDamageMultiplier: 0.25 },
  },
  {
    id: "relic_dividend",
    name: "遗迹分红",
    description: "考古学识转化为更高矿脉收益和持续工资。",
    requirements: [{ kind: "tag_count", tag: "archaeology", count: 2 }],
    bonuses: { archaeologyMultiplier: 0.5, wageShareAdd: 0.08, goldMultiplier: 0.12 },
  },
  {
    id: "four_seals",
    name: "四印归一",
    description: "集齐四色秘钥后视作掌握全部秘藏，并获得巨额综合强化。",
    requirements: [
      { kind: "module", moduleId: "crimson_key" },
      { kind: "module", moduleId: "azure_key" },
      { kind: "module", moduleId: "verdant_key" },
      { kind: "module", moduleId: "golden_key" },
    ],
    bonuses: {
      damageMultiplier: 0.3,
      maxHealthAdd: 400,
      manaMaxAdd: 120,
      goldMultiplier: 0.35,
    },
    special: "all_chest_keys",
  },
  {
    id: "alchemists_compound",
    name: "炼金复利",
    description: "三种经济秘印让金币、工资和结晶获取相互增幅。",
    requirements: [
      {
        kind: "one_of",
        moduleIds: ["field_ledger", "guild_bond", "appraisers_monocle"],
      },
      { kind: "tag_count", tag: "finance", count: 3 },
    ],
    bonuses: { goldMultiplier: 0.28, wageShareAdd: 0.05, crystalDropChance: 0.012 },
  },
  {
    id: "living_memory",
    name: "生生不息",
    description: "多条成长路线并行时，成长效率与生存能力提高。",
    requirements: [{ kind: "tag_count", tag: "growth", count: 3 }],
    bonuses: { damageMultiplier: 0.15, maxHealthAdd: 320, armorAdd: 18 },
  },
  {
    id: "ashen_court",
    name: "灰烬王庭",
    description: "凤凰与火焰、治疗共同构成终局王庭；升格后威力进一步跃迁。",
    requirements: [
      { kind: "one_of", moduleIds: ["phoenix_horn", "ashen_crown"] },
      { kind: "tag_count", tag: "fire", count: 2 },
      { kind: "tag_count", tag: "healing", count: 2 },
    ],
    bonuses: { damageMultiplier: 0.28, healingPerSecond: 15, companionDamageMultiplier: 0.55 },
    special: "companion_rebirth_burst",
    apex: true,
  },
  {
    id: "storm_sovereign",
    name: "雷霆君临",
    description: "风暴王冠统御全部闪电；升格后连锁覆盖全场。",
    requirements: [
      { kind: "one_of", moduleIds: ["storm_crown", "eye_of_tempest"] },
      { kind: "tag_count", tag: "lightning", count: 2 },
    ],
    bonuses: { damageMultiplier: 0.32, chainTargets: 3, fireIntervalMultiplier: 0.84 },
    special: "portal_on_hit",
    apex: true,
  },
  {
    id: "worldbreaker",
    name: "碎界锻炉",
    description: "魔神熔炉将强化与成长压缩成终极本体构筑。",
    requirements: [
      { kind: "one_of", moduleIds: ["demon_forge", "worldbreaker_relic"] },
      { kind: "tag_count", tag: "enhancement", count: 2 },
      { kind: "tag_count", tag: "growth", count: 2 },
    ],
    bonuses: { damageMultiplier: 0.4, maxHealthAdd: 600, armorAdd: 35 },
    special: "emergency_overdrive",
    apex: true,
  },
  {
    id: "eternal_hunt",
    name: "永恒荒猎",
    description: "星界牧园率领牧灵踏入神话狩猎。",
    requirements: [
      { kind: "one_of", moduleIds: ["celestial_menagerie", "wild_hunt"] },
      { kind: "tag_count", tag: "pasture", count: 2 },
    ],
    bonuses: { companionDamageMultiplier: 0.8, goldMultiplier: 0.25 },
    special: "pasture_inherits_attack",
    apex: true,
  },
  {
    id: "legion_throne",
    name: "军团王座",
    description: "大法师之门与召唤契约组成可持续的终局军团。",
    requirements: [
      { kind: "one_of", moduleIds: ["archmage_gate", "throne_of_legions"] },
      { kind: "tag_count", tag: "summon", count: 2 },
    ],
    bonuses: {
      companionDamageMultiplier: 0.75,
      manaRegenAdd: 6,
      summonCapAdd: 8,
    },
    special: "summon_echoes",
    apex: true,
  },
];

export const RETIRED_DEFAULT_SYNERGIES: SynergyDefinition[] = [
  ...applySkillIdentityOverridesToSynergies(
    applySkillPoolCurationToSynergies(
      expandSynergiesForSkillPool(
        [...BASE_SYNERGIES, ...EXPANDED_SYNERGIES],
        NORMALIZED_PRE_IDENTITY_MODULES,
      ),
      NORMALIZED_PRE_IDENTITY_MODULES,
      CURATED_PRE_IDENTITY_MODULES,
      skillPoolCurationDocument,
    ),
    CURATED_PRE_IDENTITY_MODULES,
    skillIdentityOverrideDocument,
  ),
];

export const RETIRED_SYNERGIES: SynergyDefinition[] = adaptSynergiesToModules(
  RETIRED_DEFAULT_SYNERGIES,
  RETIRED_MODULES,
);
export const DEFAULT_SYNERGIES: SynergyDefinition[] = NEXT_SYNERGIES;
export const SYNERGIES: SynergyDefinition[] = adaptSynergiesToModules(
  applySynergyOverrides(DEFAULT_SYNERGIES, skillOverrideDocument),
  MODULES,
);

function createTeamTechLevels(
  effect: (level: number) => string,
  baseCost = 140,
  costStep = 35,
): TeamTechDefinition["levels"] {
  return Array.from({ length: 10 }, (_, index) => {
    const level = index + 1;
    return {
      cost: baseCost + index * costStep,
      effect: effect(level),
    };
  });
}

export const TEAM_TECH: TeamTechDefinition[] = [
  {
    id: "core_vitality",
    name: "世界树根脉",
    description: "提高主基地生命上限。",
    levels: createTeamTechLevels((level) => `基地生命上限累计 +${level * 300}`),
  },
  {
    id: "core_armor",
    name: "星铁核心",
    description: "提高主基地护甲。",
    levels: createTeamTechLevels((level) => `基地护甲累计 +${level * 3}`),
  },
  {
    id: "war_chant",
    name: "百战颂歌",
    description: "提高全体玩家造成的伤害。",
    levels: createTeamTechLevels((level) => `全员攻击伤害累计 +${level * 3}%`),
  },
  {
    id: "haste_chant",
    name: "疾风节拍",
    description: "提高全体玩家攻击速度。",
    levels: createTeamTechLevels((level) => `全员攻击速度累计 +${level * 2}%`),
  },
  {
    id: "familiar_fury",
    name: "使魔战契",
    description: "提高牧灵、随从、召唤物与护卫伤害。",
    levels: createTeamTechLevels((level) => `全员召唤单位伤害累计 +${level * 4}%`),
  },
  {
    id: "familiar_ward",
    name: "灵契甲胄",
    description: "提高所有可受伤召唤单位的护甲。",
    levels: createTeamTechLevels((level) => `全员召唤单位护甲累计 +${level * 3}`),
  },
  {
    id: "guardian_oath",
    name: "守护者誓约",
    description: "提高全体玩家防御力。",
    levels: createTeamTechLevels((level) => `全员护甲累计 +${level * 3}`),
  },
  {
    id: "tower_power",
    name: "鹰眼弩臂",
    description: "提高所有箭塔攻击力。",
    levels: createTeamTechLevels((level) => `箭塔攻击力累计 +${level * 8}%`),
  },
  {
    id: "tower_haste",
    name: "风轮绞盘",
    description: "提高所有箭塔攻击速度。",
    levels: createTeamTechLevels((level) => `箭塔攻击速度累计 +${level * 3}%`),
  },
  {
    id: "tower_ward",
    name: "塔楼护符",
    description: "提高承载箭塔墙段的护甲。",
    levels: createTeamTechLevels((level) => `箭塔墙段护甲累计 +${level * 3}`),
  },
  {
    id: "tower_vitality",
    name: "活木塔基",
    description: "提高承载箭塔墙段的生命上限。",
    levels: createTeamTechLevels((level) => `箭塔墙段生命累计 +${level * 180}`),
  },
  {
    id: "wall_vitality",
    name: "巨岩砌术",
    description: "提高所有城墙生命上限。",
    levels: createTeamTechLevels((level) => `城墙生命累计 +${level * 140}`),
  },
  {
    id: "wall_armor",
    name: "符文灰浆",
    description: "提高所有城墙护甲。",
    levels: createTeamTechLevels((level) => `城墙护甲累计 +${level * 2}`),
  },
];

export const ENEMIES: EnemyDefinition[] = [
  {
    id: "rift_invader", name: "虚空侵袭体", rank: "normal", color: 0x9d63df,
    radius: 14, health: 70, speed: 76, coreDamage: 10, attackIntervalMs: 800,
    scrapReward: 2, experienceReward: 4, coreReward: 0, unlockAtMs: 0, weight: 0,
    spawnSource: "objective",
  },
  {
    id: "moss_crawler", name: "王国步兵", rank: "normal", color: 0x657180,
    radius: 15, health: 62, speed: 58, coreDamage: 8, attackIntervalMs: 900,
    scrapReward: 3, experienceReward: 5, coreReward: 0, unlockAtMs: 0, weight: 100,
  },
  {
    id: "ember_hopper", name: "轻装斥候", rank: "normal", color: 0x657180,
    radius: 12, health: 44, speed: 98, coreDamage: 6, attackIntervalMs: 700,
    scrapReward: 3, experienceReward: 5, coreReward: 0, unlockAtMs: 45_000, weight: 74,
  },
  {
    id: "gnawling", name: "雇佣刀客", rank: "normal", color: 0x657180,
    radius: 13, health: 52, speed: 82, coreDamage: 7, attackIntervalMs: 650,
    scrapReward: 3, experienceReward: 6, coreReward: 0, unlockAtMs: 90_000, weight: 82,
  },
  {
    id: "stone_brute", name: "重盾卫士", rank: "normal", color: 0x657180,
    radius: 25, health: 220, armor: 8, speed: 35, coreDamage: 18, attackIntervalMs: 1_250,
    scrapReward: 8, experienceReward: 13, coreReward: 0, unlockAtMs: 130_000, weight: 48,
  },
  {
    id: "bog_sprinter", name: "王国先锋", rank: "normal", color: 0x657180,
    radius: 14, health: 72, speed: 118, coreDamage: 10, attackIntervalMs: 620,
    scrapReward: 5, experienceReward: 8, coreReward: 0, unlockAtMs: 180_000, weight: 60,
    ability: "charge",
  },
  {
    id: "spore_pod", name: "随军旗手", rank: "normal", color: 0x657180,
    radius: 19, health: 126, speed: 45, coreDamage: 11, attackIntervalMs: 1_000,
    scrapReward: 6, experienceReward: 9, coreReward: 0, unlockAtMs: 230_000, weight: 56,
    splitInto: "moss_crawler",
  },
  {
    id: "crystal_beetle", name: "圣钢盾卫", rank: "normal", color: 0x657180,
    radius: 18, health: 155, armor: 18, speed: 48, coreDamage: 12, attackIntervalMs: 900,
    scrapReward: 7, experienceReward: 11, coreReward: 0, unlockAtMs: 300_000, weight: 45,
  },
  {
    id: "hex_wisp", name: "战场祭司", rank: "normal", color: 0x657180,
    radius: 14, health: 92, speed: 70, coreDamage: 13, attackIntervalMs: 800,
    scrapReward: 5, experienceReward: 10, coreReward: 0, unlockAtMs: 360_000, weight: 58,
    ability: "drain_mana",
  },
  {
    id: "shade_archer", name: "长弓游侠", rank: "normal", color: 0x657180,
    radius: 15, health: 105, speed: 62, coreDamage: 15, attackIntervalMs: 760,
    scrapReward: 6, experienceReward: 11, coreReward: 0, unlockAtMs: 450_000, weight: 55,
  },
  {
    id: "plague_sack", name: "炼金投毒手", rank: "normal", color: 0x657180,
    radius: 22, health: 240, speed: 40, coreDamage: 20, attackIntervalMs: 1_100,
    scrapReward: 9, experienceReward: 15, coreReward: 0, unlockAtMs: 560_000, weight: 42,
    ability: "slow",
  },
  {
    id: "iron_tusk", name: "破城重甲兵", rank: "normal", color: 0x657180,
    radius: 27, health: 360, armor: 22, speed: 52, coreDamage: 26, attackIntervalMs: 1_000,
    scrapReward: 12, experienceReward: 19, coreReward: 0, unlockAtMs: 700_000, weight: 34,
  },
  {
    id: "void_moth", name: "护旗骑士", rank: "normal", color: 0x657180,
    radius: 20, health: 205, speed: 88, coreDamage: 22, attackIntervalMs: 720,
    scrapReward: 9, experienceReward: 18, coreReward: 0, unlockAtMs: 850_000, weight: 40,
    ability: "shield_aura",
  },
  {
    id: "ivory_spellguard", name: "象牙咒卫", rank: "normal", color: 0xf1e6ca,
    radius: 14, health: 92 * 1.2, speed: 70 * 1.2, coreDamage: 13 * 1.2,
    attackIntervalMs: 800 / 1.2,
    scrapReward: 5, experienceReward: 10, coreReward: 0,
    unlockAtMs: 360_000, weight: 8, ability: "drain_mana",
    normalStyle: "special", specialBasedOnEnemyId: "hex_wisp", attributeMultiplier: 1.2,
  },
  {
    id: "blackiron_breaker", name: "黑铁破城兵", rank: "normal", color: 0x343b48,
    radius: 25, health: 220 * 1.2, armor: 8 * 1.2, speed: 35 * 1.2,
    coreDamage: 18 * 1.2, attackIntervalMs: 1_250 / 1.2,
    scrapReward: 8, experienceReward: 13, coreReward: 0,
    unlockAtMs: 520_000, weight: 6,
    normalStyle: "special", specialBasedOnEnemyId: "stone_brute", attributeMultiplier: 1.2,
  },
  {
    id: "thorn_guard", name: "荆冠骑士", rank: "elite", color: 0xe8bd54,
    radius: 34, health: 1_050, armor: 18, speed: 40, coreDamage: 32, attackIntervalMs: 1_100,
    scrapReward: 45, experienceReward: 80, coreReward: 2, unlockAtMs: 70_000, weight: 1,
    ability: "shield_aura",
  },
  {
    id: "mana_eater", name: "裁魔审判官", rank: "elite", color: 0xf1e6ca,
    radius: 30, health: 900, speed: 58, coreDamage: 28, attackIntervalMs: 850,
    scrapReward: 42, experienceReward: 78, coreReward: 2, unlockAtMs: 240_000, weight: 1,
    ability: "drain_mana",
  },
  {
    id: "grave_charger", name: "赤锋骑士", rank: "elite", color: 0xb83b42,
    radius: 38, health: 1_450, armor: 20, speed: 68, coreDamage: 42, attackIntervalMs: 1_050,
    scrapReward: 58, experienceReward: 105, coreReward: 3, unlockAtMs: 480_000, weight: 1,
    ability: "charge",
  },
  {
    id: "prism_warden", name: "皇家督军", rank: "elite", color: 0xe8bd54,
    radius: 35, health: 1_900, armor: 34, speed: 45, coreDamage: 46, attackIntervalMs: 900,
    scrapReward: 65, experienceReward: 125, coreReward: 3, unlockAtMs: 720_000, weight: 1,
    ability: "spawn_minions",
  },
  {
    id: "elder_maw", name: "贪金大主教", rank: "boss", color: 0xf1e6ca,
    radius: 62, health: 13_500, armor: 22, speed: 28, coreDamage: 72, attackIntervalMs: 1_300,
    scrapReward: 260, experienceReward: 450, coreReward: 10, unlockAtMs: 590_000, weight: 1,
    ability: "spawn_minions",
  },
  {
    id: "siege_marshal", name: "王国破城元帅·赫克托", rank: "boss", color: 0x4f6176,
    radius: 76, health: 72_000, armor: 46, speed: 26, coreDamage: 132,
    attackIntervalMs: 1_250, scrapReward: 500, experienceReward: 780,
    coreReward: 0, unlockAtMs: 720_000, weight: 0, ability: "siege_marshal",
  },
  {
    id: "storm_tyrant", name: "远征王·奥雷恩", rank: "boss", color: 0xe8bd54,
    radius: 74, health: 120_000, armor: 38, speed: 30, coreDamage: 105, attackIntervalMs: 1_100,
    scrapReward: 520, experienceReward: 850, coreReward: 18, unlockAtMs: 1_080_000, weight: 1,
    ability: "shield_aura",
  },
];

export const DIFFICULTIES: Record<string, DifficultyDefinition> = {
  apprentice: {
    id: "apprentice",
    name: "学徒",
    enemyHealthMultiplier: 0.82,
    enemyDamageMultiplier: 0.82,
    spawnMultiplier: 0.9,
    rewardMultiplier: 1.18,
    eliteMultiplier: 0.9,
    bossHealthMultiplier: 0.66,
    archaeologyMultiplier: 1.15,
  },
  veteran: {
    id: "veteran",
    name: "远征者",
    enemyHealthMultiplier: 1.08,
    enemyDamageMultiplier: 1.08,
    spawnMultiplier: 1.08,
    rewardMultiplier: 1,
    eliteMultiplier: 1.1,
    bossHealthMultiplier: 1,
    archaeologyMultiplier: 1,
  },
  master: {
    id: "master",
    name: "神话",
    enemyHealthMultiplier: 1.42,
    enemyDamageMultiplier: 1.38,
    spawnMultiplier: 1.16,
    rewardMultiplier: 0.84,
    eliteMultiplier: 1.65,
    bossHealthMultiplier: 1,
    archaeologyMultiplier: 0.88,
  },
};

export const ALL_MODULE_DEFINITIONS: ModuleDefinition[] = [
  ...RETIRED_MODULES,
  ...MODULES,
];

const moduleById = new Map(
  ALL_MODULE_DEFINITIONS.map((module) => [module.id, module]),
);
const synergyById = new Map(SYNERGIES.map((synergy) => [synergy.id, synergy]));
const enemyById = new Map(ENEMIES.map((enemy) => [enemy.id, enemy]));
const teamTechById = new Map(TEAM_TECH.map((tech) => [tech.id, tech]));

export function getModule(id: string): ModuleDefinition {
  const definition = moduleById.get(id);
  if (!definition) {
    throw new Error(`Unknown ability: ${id}`);
  }
  return definition;
}

export const getAbility = getModule;

export function getSynergy(id: string): SynergyDefinition {
  const definition = synergyById.get(id);
  if (!definition) {
    throw new Error(`Unknown synergy: ${id}`);
  }
  return definition;
}

export function getEnemy(id: string): EnemyDefinition {
  const definition = enemyById.get(id);
  if (!definition) {
    throw new Error(`Unknown enemy: ${id}`);
  }
  return definition;
}

export function getTeamTech(id: string): TeamTechDefinition {
  const definition = teamTechById.get(id as TeamTechDefinition["id"]);
  if (!definition) {
    throw new Error(`Unknown team tech: ${id}`);
  }
  return definition;
}

function requirementMet(
  requirement: SynergyRequirement,
  moduleIds: string[],
  activeSynergyIds: ReadonlySet<string>,
): boolean {
  switch (requirement.kind) {
    case "module":
      return moduleIds.includes(requirement.moduleId);
    case "synergy":
      return activeSynergyIds.has(requirement.synergyId);
    case "one_of":
      return requirement.moduleIds.some((id) => moduleIds.includes(id));
    case "tag_count":
      return (
        new Set(
          moduleIds.filter((id) =>
            getModule(id).tags.includes(requirement.tag),
          ),
        ).size >= requirement.count
      );
    case "tier_count":
      return moduleIds.filter((id) => getModule(id).tier >= requirement.minTier).length >=
        requirement.count;
  }
}

export function evaluateSynergies(
  moduleIds: Array<string | null>,
): SynergyDefinition[] {
  const owned = moduleIds.filter((id): id is string => Boolean(id));
  const active = new Set<string>();
  let changed = true;
  while (changed) {
    changed = false;
    for (const synergy of SYNERGIES) {
      if (
        active.has(synergy.id) ||
        !synergy.requirements.every((requirement) =>
          requirementMet(requirement, owned, active),
        )
      ) {
        continue;
      }
      active.add(synergy.id);
      changed = true;
    }
  }
  return SYNERGIES.filter((synergy) => active.has(synergy.id));
}

const abilityTuningCache = new Map<string, AbilityTuning>();

export function getAbilityTuning(
  moduleIds: Array<string | null>,
  moduleId: string,
): AbilityTuning {
  const cacheKey = `${moduleIds.map((id) => id ?? "-").join("|")}::${moduleId}`;
  const cached = abilityTuningCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const tuning: AbilityTuning = {
    damageMultiplier: 1,
    intervalMultiplier: 1,
    rangeMultiplier: 1,
    radiusAdd: 0,
    projectilesAdd: 0,
    extraTargetsAdd: 0,
    manaCostMultiplier: 1,
    companionCountAdd: 0,
    companionHealthMultiplier: 1,
    companionSpawnIntervalMultiplier: 1,
    cannonBaseDiameterMultiplier: 1,
  };
  for (const synergy of evaluateSynergies(moduleIds)) {
    for (const modifier of synergy.abilityModifiers ?? []) {
      if (!modifier.moduleIds.includes(moduleId)) {
        continue;
      }
      tuning.damageMultiplier *= modifier.damageMultiplier ?? 1;
      tuning.intervalMultiplier *= modifier.intervalMultiplier ?? 1;
      tuning.rangeMultiplier *= modifier.rangeMultiplier ?? 1;
      tuning.radiusAdd += modifier.radiusAdd ?? 0;
      tuning.projectilesAdd += modifier.projectilesAdd ?? 0;
      tuning.extraTargetsAdd += modifier.extraTargetsAdd ?? 0;
      tuning.manaCostMultiplier *= modifier.manaCostMultiplier ?? 1;
      tuning.companionCountAdd += modifier.companionCountAdd ?? 0;
      tuning.companionHealthMultiplier *=
        modifier.companionHealthMultiplier ?? 1;
      tuning.companionSpawnIntervalMultiplier *=
        modifier.companionSpawnIntervalMultiplier ?? 1;
      tuning.cannonBaseDiameterMultiplier *=
        modifier.cannonBaseDiameterMultiplier ?? 1;
    }
  }
  if (abilityTuningCache.size >= 5_000) {
    abilityTuningCache.clear();
  }
  abilityTuningCache.set(cacheKey, tuning);
  return tuning;
}

export function describeRequirement(requirement: SynergyRequirement): string {
  switch (requirement.kind) {
    case "module":
      return getModule(requirement.moduleId).name;
    case "synergy":
      return getSynergy(requirement.synergyId).name;
    case "one_of":
      return requirement.moduleIds.map((id) => getModule(id).name).join(" / ");
    case "tag_count":
      return `${synergyTagLabel(requirement.tag)} ×${requirement.count}`;
    case "tier_count":
      return `${requirement.minTier}阶以上 ×${requirement.count}`;
  }
}

function synergyTagLabel(tag: string): string {
  return {
    pasture_spirit: "牧灵",
  }[tag] ?? "同系技能";
}

export function applyStatBonuses(
  target: PlayerStats,
  bonuses: StatBonuses,
  effectScale = 1,
): void {
  const add = (value: number | undefined): number => (value ?? 0) * effectScale;
  const multiplier = (value: number | undefined): number =>
    1 + ((value ?? 1) - 1) * effectScale;
  target.damage += add(bonuses.damageAdd);
  target.damageMultiplier += add(bonuses.damageMultiplier);
  target.attackDamageMultiplier += add(bonuses.attackDamageBonus);
  target.abilityDamageMultiplier += add(bonuses.abilityDamageBonus);
  target.baseAttackSpeedModifier += add(bonuses.baseAttackSpeedModifier);
  target.attackSpeedBonus += add(bonuses.attackSpeedBonus);
  target.fireIntervalMs *= multiplier(bonuses.fireIntervalMultiplier);
  target.range += add(bonuses.rangeAdd);
  target.moveSpeed += add(bonuses.moveSpeedAdd);
  target.moveSpeedMultiplier *= multiplier(bonuses.moveSpeedMultiplier);
  target.maxHealth += add(bonuses.maxHealthAdd) + 260 * add(bonuses.baseMaxHealthRatio);
  target.armor += add(bonuses.armorAdd);
  target.dodge += add(bonuses.dodgeAdd);
  target.goldMultiplier += add(
    bonuses.goldMultiplier ?? bonuses.scrapMultiplier,
  );
  target.scrapMultiplier = target.goldMultiplier;
  target.experienceMultiplier += add(bonuses.experienceMultiplier);
  target.repairPerSecond += add(bonuses.repairPerSecond);
  target.healingPerSecond += add(bonuses.healingPerSecond);
  target.healthRegen += add(bonuses.healthRegenAdd);
  target.healthRegenMaxRatio += add(bonuses.healthRegenMaxRatio);
  target.manaMax += add(bonuses.manaMaxAdd);
  target.manaRegen += add(bonuses.manaRegenAdd);
  target.manaRegenMaxRatio += add(bonuses.manaRegenMaxRatio);
  target.scoutingRadius += add(bonuses.scoutingRadiusAdd);
  target.splashRadius = Math.max(target.splashRadius, add(bonuses.splashRadius));
  target.chainTargets += add(bonuses.chainTargets);
  target.pierceTargets += add(bonuses.pierceTargets);
  target.crystalDropChance += add(
    bonuses.crystalDropChance ?? bonuses.coreDropChance,
  );
  target.coreDropChance = target.crystalDropChance;
  target.eliteDamageMultiplier += add(bonuses.eliteDamageMultiplier);
  target.companionDamageMultiplier += add(bonuses.companionDamageMultiplier);
  target.summonCap += add(bonuses.summonCapAdd);
  target.revealRadius += add(bonuses.revealRadius);
  target.archaeologyMultiplier += add(bonuses.archaeologyMultiplier);
  target.wageShare += add(bonuses.wageShareAdd);
}

export const PLAYER_BASE_MOVE_SPEED = 265;

export function computePlayerStats(moduleIds: Array<string | null>): PlayerStats {
  const stats: PlayerStats = {
    damage: 20,
    damageMultiplier: 1,
    attackDamageMultiplier: 1,
    abilityDamageMultiplier: 1,
    baseAttackSpeedModifier: 0,
    attackSpeedBonus: 0,
    fireIntervalMs: 650,
    range: 380,
    moveSpeed: PLAYER_BASE_MOVE_SPEED,
    moveSpeedMultiplier: 1,
    maxHealth: 260,
    armor: 0,
    dodge: 0,
    goldMultiplier: 1,
    scrapMultiplier: 1,
    experienceMultiplier: 1,
    // Every hero can maintain monster-city buildings when standing nearby.
    repairPerSecond: 6,
    healingPerSecond: 0,
    healthRegen: 0.5,
    healthRegenMaxRatio: 0,
    manaMax: 100,
    manaRegen: 0.55,
    manaRegenMaxRatio: 0,
    scoutingRadius: 650,
    splashRadius: 0,
    chainTargets: 0,
    pierceTargets: 0,
    crystalDropChance: 0.004,
    coreDropChance: 0.004,
    eliteDamageMultiplier: 1,
    companionDamageMultiplier: 1,
    summonCap: 10,
    revealRadius: 0,
    surfaceMiningIntervalMultiplier: 1,
    surfaceMiningExtraGoldFlat: 0,
    surfaceMiningExtraGoldMultiplier: 0,
    surfaceMiningSpeedBonus: 0,
    surfaceMiningGoldYieldBonus: 0,
    surfaceMiningRevealRadiusBonus: 0,
    surfaceMiningCrystalYieldRatio: 0,
    surfaceMiningManaSpeedBonus: 0,
    surfaceMiningManaCostPerSettlement: 0,
    archaeologyMultiplier: 1,
    wageShare: 0.05,
  };

  const ownedModules = moduleIds.flatMap((moduleId) =>
    moduleId ? [getModule(moduleId)] : [],
  );
  const pastureEffectMultiplier = Math.max(
    1,
    ...ownedModules.flatMap((module) =>
      (module.mechanics ?? []).flatMap((mechanic) =>
        mechanic.kind === "pasture_effect_amplifier"
          ? [mechanic.effectMultiplier]
          : [],
      ),
    ),
  );
  const activeSynergies = evaluateSynergies(moduleIds);
  const miningAmplifier = activeSynergies
    .flatMap((synergy) => synergy.mechanics ?? [])
    .find((mechanic) => mechanic.kind === "surface_mining_skill_amplifier");
  for (const module of ownedModules) {
    applyStatBonuses(
      stats,
      module.bonuses,
      module.category === "pasture" ? pastureEffectMultiplier : 1,
    );
    if (module.surfaceMining) {
      const miningEffectMultiplier =
        miningAmplifier?.kind === "surface_mining_skill_amplifier" &&
        !miningAmplifier.excludedModuleIds.includes(module.id)
          ? miningAmplifier.positiveEffectMultiplier
          : 1;
      stats.surfaceMiningIntervalMultiplier *=
        module.surfaceMining.settlementIntervalMultiplier ?? 1;
      stats.surfaceMiningExtraGoldFlat +=
        (module.surfaceMining.extraGoldFlat ?? 0) * miningEffectMultiplier;
      stats.surfaceMiningExtraGoldMultiplier +=
        (module.surfaceMining.extraGoldMultiplier ?? 0) * miningEffectMultiplier;
      stats.surfaceMiningSpeedBonus +=
        ((module.surfaceMining.speedBonus ?? 0) +
          (module.surfaceMining.settlementIntervalMultiplier
            ? 1 / module.surfaceMining.settlementIntervalMultiplier - 1
            : 0)) *
        miningEffectMultiplier;
      stats.surfaceMiningGoldYieldBonus +=
        (module.surfaceMining.goldYieldBonus ?? 0) * miningEffectMultiplier;
      stats.surfaceMiningRevealRadiusBonus +=
        (module.surfaceMining.conditionalRevealRadiusBonus ?? 0) *
        miningEffectMultiplier;
      stats.surfaceMiningCrystalYieldRatio +=
        (module.surfaceMining.crystalYieldRatio ?? 0) *
        miningEffectMultiplier;
      if (module.surfaceMining.manaPoweredSpeed) {
        stats.surfaceMiningManaSpeedBonus +=
          module.surfaceMining.manaPoweredSpeed.speedBonus *
          miningEffectMultiplier;
        stats.surfaceMiningManaCostPerSettlement +=
          module.surfaceMining.manaPoweredSpeed.manaCostPerSettlement;
      }
      stats.revealRadius +=
        (module.surfaceMining.revealRadiusAdd ?? 0) * miningEffectMultiplier;
      stats.scoutingRadius +=
        (module.surfaceMining.scoutingRadiusAdd ?? 0) * miningEffectMultiplier;
    }
  }
  for (const synergy of activeSynergies) {
    applyStatBonuses(stats, synergy.bonuses);
  }
  stats.manaMax *= ownedModules.reduce(
    (product, module) => product * (module.bonuses.manaMaxMultiplier ?? 1),
    1,
  );

  stats.fireIntervalMs = Math.max(120, stats.fireIntervalMs);
  stats.abilityDamageMultiplier = Math.max(0, stats.abilityDamageMultiplier);
  stats.attackDamageMultiplier = Math.max(0, stats.attackDamageMultiplier);
  stats.baseAttackSpeedModifier = Math.max(-0.95, stats.baseAttackSpeedModifier);
  stats.attackSpeedBonus = Math.max(-0.95, stats.attackSpeedBonus);
  stats.moveSpeedMultiplier = Math.max(0, stats.moveSpeedMultiplier);
  stats.manaRegenMaxRatio = Math.max(0, stats.manaRegenMaxRatio);
  stats.healthRegenMaxRatio = Math.max(0, stats.healthRegenMaxRatio);
  stats.goldMultiplier = Math.max(0.2, stats.goldMultiplier);
  stats.scrapMultiplier = stats.goldMultiplier;
  stats.experienceMultiplier = Math.max(0.2, stats.experienceMultiplier);
  stats.dodge = Math.min(0.55, Math.max(0, stats.dodge));
  stats.armor = Math.max(0, stats.armor);
  stats.summonCap = Math.max(1, Math.min(28, stats.summonCap));
  stats.revealRadius = Math.max(0, stats.revealRadius);
  stats.surfaceMiningIntervalMultiplier = Math.max(
    0.1,
    stats.surfaceMiningIntervalMultiplier,
  );
  stats.surfaceMiningExtraGoldFlat = Math.max(
    0,
    stats.surfaceMiningExtraGoldFlat,
  );
  stats.surfaceMiningExtraGoldMultiplier = Math.max(
    0,
    stats.surfaceMiningExtraGoldMultiplier,
  );
  stats.surfaceMiningSpeedBonus = Math.max(0, stats.surfaceMiningSpeedBonus);
  stats.surfaceMiningGoldYieldBonus = Math.max(
    0,
    stats.surfaceMiningGoldYieldBonus,
  );
  stats.surfaceMiningRevealRadiusBonus = Math.max(
    0,
    stats.surfaceMiningRevealRadiusBonus,
  );
  stats.surfaceMiningCrystalYieldRatio = Math.max(
    0,
    stats.surfaceMiningCrystalYieldRatio,
  );
  stats.surfaceMiningManaSpeedBonus = Math.max(
    0,
    stats.surfaceMiningManaSpeedBonus,
  );
  stats.surfaceMiningManaCostPerSettlement = Math.max(
    0,
    stats.surfaceMiningManaCostPerSettlement,
  );
  return stats;
}

export function getAbilityDps(module: ModuleDefinition): number {
  if (!module.combat) {
    return 0;
  }
  return (
    ((module.combat.damage * (module.combat.hits ?? 1) +
      (module.combat.delayedEcho?.damage ?? 0)) *
      (module.combat.projectiles ?? 1) *
      1_000) /
    module.combat.intervalMs
  );
}

export function validateContent(): string[] {
  const errors: string[] = [
    ...validateHiddenEffectContent(),
    ...validateUndeadUnitContent(),
  ];
  const ids = new Set<string>();
  for (const enemy of ENEMIES) {
    if (enemy.normalStyle !== "special") continue;
    const base = ENEMIES.find(
      (candidate) => candidate.id === enemy.specialBasedOnEnemyId,
    );
    if (
      enemy.rank !== "normal" ||
      !base ||
      base.rank !== "normal" ||
      enemy.attributeMultiplier !== 1.2 ||
      base.normalStyle === "special"
    ) {
      errors.push(`特殊普通敌人定义无效: ${enemy.id}`);
      continue;
    }
    const ratioChecks = [
      enemy.health / base.health,
      enemy.speed / base.speed,
      enemy.coreDamage / base.coreDamage,
      base.attackIntervalMs / enemy.attackIntervalMs,
      base.armor === undefined && enemy.armor === undefined
        ? 1.2
        : (enemy.armor ?? 0) / Math.max(0.001, base.armor ?? 0),
    ];
    if (ratioChecks.some((ratio) => Math.abs(ratio - 1.2) > 1e-9)) {
      errors.push(`特殊普通敌人未严格提高20%战斗属性: ${enemy.id}`);
    }
  }
  const conditionalEvolutionTargets = new Set(
    MODULES.flatMap((module) =>
      module.conditionalEvolution
        ? [module.conditionalEvolution.targetModuleId]
        : [],
    ),
  );
  for (const module of MODULES) {
    if (ids.has(module.id)) {
      errors.push(`重复技能ID: ${module.id}`);
    }
    ids.add(module.id);
    if (
      module.tier === 6 &&
      !module.evolutionFrom &&
      !conditionalEvolutionTargets.has(module.id) &&
      !module.stateFamily?.inheritsSlotLock
    ) {
      errors.push(`六阶技能缺少来源: ${module.id}`);
    }
    if (
      module.tier === 5 &&
      !module.evolutionTo &&
      !module.stateFamily?.inheritsAscension
    ) {
      errors.push(`五阶技能缺少升格目标: ${module.id}`);
    }
    if (module.evolutionTo) {
      const target = MODULES.find((candidate) => candidate.id === module.evolutionTo);
      if (!target || target.tier !== 6 || target.evolutionFrom !== module.id) {
        errors.push(`升格映射不完整: ${module.id} -> ${module.evolutionTo}`);
      }
    }
    if (
      module.combat &&
      !isAllowedAttackIntervalMs(module.combat.intervalMs)
    ) {
      errors.push(
        `技能 ${module.id} 的攻击间隔不符合项目精度规则: ${module.combat.intervalMs}ms`,
      );
    }
    if (
      module.companion?.intervalMs !== undefined &&
      !isAllowedAttackIntervalMs(module.companion.intervalMs)
    ) {
      errors.push(
        `技能 ${module.id} 的伙伴攻击间隔不符合项目精度规则: ${module.companion.intervalMs}ms`,
      );
    }
    for (const entry of getModuleSkillRangeEntries(module)) {
      if (!isAllowedSkillRange(entry.value)) {
        errors.push(
          `技能 ${module.id} 的${entry.label}不符合项目范围档位规则: ${entry.value}`,
        );
      }
    }
    for (const message of getModuleDpsErrors(module, MODULES)) {
      errors.push(`技能 ${module.id} 的DPS不符合项目规则: ${message}`);
    }
  }
  const hiddenEffectIds = new Set(HIDDEN_EFFECTS.map((effect) => effect.id));
  for (const module of ALL_MODULE_DEFINITIONS) {
    for (const effectId of module.grantsHiddenEffectIds ?? []) {
      if (!hiddenEffectIds.has(effectId)) {
        errors.push(`技能 ${module.id} 授予了未知隐藏效果 ${effectId}`);
      }
    }
    const repeatableEffectId = module.onAcquire?.repeatableHiddenEffectId;
    if (repeatableEffectId) {
      const effect = HIDDEN_EFFECTS.find(
        (candidate) => candidate.id === repeatableEffectId,
      );
      if (!effect?.repeatable) {
        errors.push(
          `技能 ${module.id} 授予了未知或不可重复的隐藏效果 ${repeatableEffectId}`,
        );
      }
    }
  }
  errors.push(...validateNextSkillLibraryContent());
  const synergyIds = new Set<string>();
  for (const synergy of SYNERGIES) {
    if (synergyIds.has(synergy.id)) {
      errors.push(`重复羁绊ID: ${synergy.id}`);
    }
    synergyIds.add(synergy.id);
    for (const requirement of synergy.requirements) {
      if (requirement.kind === "module" && !ids.has(requirement.moduleId)) {
        errors.push(`羁绊 ${synergy.id} 引用了未知技能 ${requirement.moduleId}`);
      }
      if (
        requirement.kind === "one_of" &&
        requirement.moduleIds.some((id) => !ids.has(id))
      ) {
        errors.push(`羁绊 ${synergy.id} 包含未知候选技能`);
      }
      if (
        requirement.kind === "synergy" &&
        !SYNERGIES.some((candidate) => candidate.id === requirement.synergyId)
      ) {
        errors.push(`羁绊 ${synergy.id} 引用了未知羁绊 ${requirement.synergyId}`);
      }
    }
    for (const modifier of synergy.abilityModifiers ?? []) {
      if (modifier.moduleIds.some((id) => !ids.has(id))) {
        errors.push(`羁绊 ${synergy.id} 的技能修正引用了未知技能`);
      }
      if (
        modifier.companionSpawnIntervalMultiplier !== undefined &&
        modifier.companionSpawnIntervalMultiplier <= 0
      ) {
        errors.push(`羁绊 ${synergy.id} 的召唤间隔倍率必须大于0`);
      }
    }
    for (const mechanic of synergy.mechanics ?? []) {
      if (
        mechanic.kind === "turret_transformation" &&
        !isAllowedSkillRange(mechanic.tauntRadius)
      ) {
        errors.push(`羁绊 ${synergy.id} 的嘲讽范围不符合范围档位规则`);
      }
      if (
        mechanic.kind === "pasture_hunt" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damagePerTotalTier <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的游猎参数不符合间隔、范围或伤害规则`);
      }
      if (
        mechanic.kind === "hidden_effect_amplifier" &&
        (mechanic.effectMultiplier < 1 ||
          mechanic.hiddenEffectIds.length === 0 ||
          mechanic.hiddenEffectIds.some((id) => !hiddenEffectIds.has(id)))
      ) {
        errors.push(`羁绊 ${synergy.id} 的隐藏效果增幅参数无效`);
      }
      if (
        mechanic.kind === "hidden_effect_amplifier" &&
        mechanic.hiddenEffectIds
          .filter((id) => hiddenEffectIds.has(id))
          .some((id) =>
            getHiddenEffect(id).effects.some(
              (effect) =>
                effect.kind === "periodic_normal_execute" &&
                !isAllowedAttackIntervalMs(
                  effect.intervalMs / mechanic.effectMultiplier,
                ),
            ),
          )
      ) {
        errors.push(`羁绊 ${synergy.id} 增幅后的隐藏效果间隔不符合项目精度规则`);
      }
      if (
        mechanic.kind === "periodic_heavy_damage" &&
        (!isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的重装打击参数不符合间隔、范围或伤害规则`);
      }
      if (mechanic.kind === "undead_catastrophe") {
        const validatePool = (
          pool: typeof mechanic.normalPool,
          label: string,
        ): void => {
          const total = pool.reduce((sum, entry) => sum + entry.weight, 0);
          if (
            pool.length === 0 ||
            total !== 100 ||
            pool.some(
              (entry) =>
                entry.weight <= 0 ||
                !UNDEAD_UNITS.some((unit) => unit.id === entry.unitId),
            )
          ) {
            errors.push(`羁绊 ${synergy.id} 的${label}亡灵权重池无效`);
          }
        };
        if (
          mechanic.summonChance < 0 ||
          mechanic.summonChance > 1 ||
          !MODULES.some(
            (module) => module.id === mechanic.triggerAbilityId,
          )
        ) {
          errors.push(`羁绊 ${synergy.id} 的亡灵天灾触发配置无效`);
        }
        validatePool(mechanic.normalPool, "普通");
        validatePool(mechanic.heavyPool, "重型");
      }
      if (
        mechanic.kind === "guard_critical" &&
        (!ids.has(mechanic.targetAbilityId) ||
          mechanic.damageAdd < 0 ||
          mechanic.attacksPerCritical < 2 ||
          !Number.isInteger(mechanic.attacksPerCritical) ||
          mechanic.criticalDamageMultiplier <= 1)
      ) {
        errors.push(`羁绊 ${synergy.id} 的护卫暴击配置无效`);
      }
      if (
        mechanic.kind === "guard_dash" &&
        (!ids.has(mechanic.targetAbilityId) ||
          mechanic.attackSpeedBonus < 0 ||
          mechanic.moveSpeedBonus < 0 ||
          !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.acquireRange) ||
          !isAllowedSkillRange(mechanic.pathWidth) ||
          mechanic.damage <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的护卫冲刺配置无效`);
      }
      if (
        mechanic.kind === "guard_oath_upgrade" &&
        (!ids.has(mechanic.targetAbilityId) ||
          !ids.has(mechanic.consumedAbilityId) ||
          mechanic.health <= 0 ||
          mechanic.damage <= 0 ||
          mechanic.armor < 0 ||
          !isAllowedAttackIntervalMs(mechanic.intervalMs) ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.moveSpeedBaseRatio <= 0 ||
          mechanic.respawnMs <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的誓约铁卫配置无效`);
      }
      if (
        mechanic.kind === "divine_guard_upgrade" &&
        (!ids.has(mechanic.targetAbilityId) ||
          mechanic.suppressedModuleIds.length === 0 ||
          mechanic.suppressedModuleIds.some((id) => !ids.has(id)) ||
          mechanic.positiveStatMultiplier < 1 ||
          mechanic.healthAdd < 0 ||
          mechanic.damageAdd < 0 ||
          mechanic.armorAdd < 0 ||
          mechanic.attackSpeedBonus < 0 ||
          mechanic.moveSpeedBonus < 0 ||
          mechanic.abilityDamageBonus < 0 ||
          !isAllowedSkillRange(mechanic.range) ||
          mechanic.respawnMs <= 0 ||
          !isAllowedAttackIntervalMs(mechanic.holyFireIntervalMs) ||
          !isAllowedSkillRange(mechanic.holyFireRadius) ||
          mechanic.holyFireDamage <= 0 ||
          mechanic.judgmentAttacksPerCritical < 2 ||
          !Number.isInteger(mechanic.judgmentAttacksPerCritical) ||
          mechanic.judgmentDamageMultiplier <= 1 ||
          !isAllowedAttackIntervalMs(mechanic.descentIntervalMs) ||
          !isAllowedSkillRange(mechanic.descentAcquireRange) ||
          !isAllowedSkillRange(mechanic.descentRadius) ||
          mechanic.descentDamage <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的神之代行者配置无效`);
      }
      if (
        mechanic.kind === "earth_spirit_aggregation" &&
        (!ids.has(mechanic.targetAbilityId) ||
          !isAllowedSkillRange(mechanic.aggregationRange) ||
          mechanic.participantsRequired !== 3 ||
          mechanic.maxStar !== 5 ||
          mechanic.baseHealth <= 0 ||
          mechanic.baseArmor < 0 ||
          mechanic.baseDamage <= 0 ||
          !isAllowedAttackIntervalMs(mechanic.baseIntervalMs) ||
          !isAllowedSkillRange(mechanic.baseRange) ||
          mechanic.baseMoveSpeedRatio <= 0 ||
          mechanic.healthMultiplierPerStar < 1 ||
          mechanic.armorMultiplierPerStar < 1 ||
          mechanic.damageMultiplierPerStar < 1 ||
          mechanic.healthRoundUpTo <= 0 ||
          mechanic.armorRoundUpTo <= 0 ||
          mechanic.damageRoundUpTo <= 0 ||
          !isAllowedSkillRange(mechanic.rulerAuraRadius) ||
          mechanic.rulerAttackSpeedBonus < 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的土灵聚合配置无效`);
      }
      if (
        mechanic.kind === "follower_granted_skill" &&
        (!ids.has(mechanic.targetAbilityId) ||
          !ids.has(mechanic.grantedAbilityId) ||
          MODULES.find((module) => module.id === mechanic.targetAbilityId)
            ?.category !== "follower" ||
          !MODULES.find((module) => module.id === mechanic.grantedAbilityId)
            ?.combat)
      ) {
        errors.push(`羁绊 ${synergy.id} 的随从技能授予配置无效`);
      }
      if (
        mechanic.kind === "day_night_cycle" &&
        (mechanic.dayDurationMs <= 0 ||
          mechanic.nightDurationMs <= 0 ||
          !isAllowedSkillRange(mechanic.range) ||
          !isAllowedAttackIntervalMs(mechanic.dayIntervalMs) ||
          !isAllowedAttackIntervalMs(mechanic.nightIntervalMs) ||
          mechanic.dayDamage <= 0 ||
          mechanic.nightDamage <= 0)
      ) {
        errors.push(`羁绊 ${synergy.id} 的日夜轮转配置无效`);
      }
      if (mechanic.kind === "greed_paradise") {
        const forcedAbility = MODULES.find(
          (module) => module.id === mechanic.forcedAggroAbilityId,
        );
        const growthAbility = MODULES.find(
          (module) => module.id === mechanic.growthAbilityId,
        );
        if (
          !forcedAbility?.mechanics?.some(
            (entry) => entry.kind === "periodic_forced_aggro",
          ) ||
          !growthAbility?.mechanics?.some(
            (entry) => entry.kind === "ability_kill_growth",
          ) ||
          !isAllowedAttackIntervalMs(mechanic.forcedDamageIntervalMs) ||
          mechanic.forcedDamage <= 0 ||
          !Number.isInteger(mechanic.killsPerDamageGrowth) ||
          mechanic.killsPerDamageGrowth < 1 ||
          mechanic.damagePerSettlement <= 0
        ) {
          errors.push(`羁绊 ${synergy.id} 的贪欲乐土配置无效`);
        }
      }
    }
    for (const entry of getSynergySkillRangeEntries(synergy)) {
      if (!isAllowedSkillRange(entry.value)) {
        errors.push(
          `羁绊 ${synergy.id} 的${entry.label}不符合项目范围档位规则: ${entry.value}`,
        );
      }
    }
  }
  const allOwned = MODULES.map((module) => module.id);
  for (const synergy of SYNERGIES) {
    if (!evaluateSynergies(allOwned).some((active) => active.id === synergy.id)) {
      errors.push(`羁绊 ${synergy.id} 存在循环依赖或无法满足的前置关系`);
    }
  }
  return errors;
}
